import { clearAppStateForDatabase } from "@excalidraw/excalidraw/appState";
import { getSceneVersion } from "@excalidraw/element";

import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";

import type { WorkFile } from "./workfiles";

type RemoteScene = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState> | null;
  files: BinaryFiles;
};

class APIError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "APIError";
    this.status = status;
  }
}

const request = async <T>(input: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(input, {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });

  let payload: any = {};

  try {
    payload = await response.json();
  } catch (error) {
    payload = {};
  }

  if (!response.ok) {
    throw new APIError(payload.error || "Request failed", response.status);
  }

  return payload as T;
};

const uploadedRemoteFileIdsByWorkFile = new Map<string, Set<FileId>>();
const lastPersistedSceneVersionByWorkFile = new Map<string, number>();

const getUploadedRemoteFileIds = (workFileId: string) => {
  let uploadedFileIds = uploadedRemoteFileIdsByWorkFile.get(workFileId);
  if (!uploadedFileIds) {
    uploadedFileIds = new Set<FileId>();
    uploadedRemoteFileIdsByWorkFile.set(workFileId, uploadedFileIds);
  }

  return uploadedFileIds;
};

export const markRemoteWorkFileFilesAsUploaded = (
  workFileId: string,
  files: BinaryFiles,
) => {
  const uploadedFileIds = getUploadedRemoteFileIds(workFileId);
  for (const fileId of Object.keys(files)) {
    uploadedFileIds.add(fileId as FileId);
  }
};

const prepareFilesForRemoteSave = (
  workFileId: string,
  files: BinaryFiles,
): BinaryFiles | undefined => {
  const uploadedFileIds = getUploadedRemoteFileIds(workFileId);
  const filesToUpload: BinaryFiles = {};

  for (const [fileId, file] of Object.entries(files)) {
    if (!file?.dataURL || uploadedFileIds.has(fileId as FileId)) {
      continue;
    }

    filesToUpload[fileId as FileId] = file as BinaryFileData;
  }

  return Object.keys(filesToUpload).length ? filesToUpload : undefined;
};

const sanitizeRemoteAppStateForLoad = (
  appState: Partial<AppState> | null,
): Partial<AppState> | null => {
  if (!appState) {
    return null;
  }

  const nextAppState = {
    ...appState,
  };

  if (
    "collaborators" in nextAppState &&
    !(nextAppState.collaborators instanceof Map)
  ) {
    delete nextAppState.collaborators;
  }

  return nextAppState;
};

export const listRemoteWorkFiles = async () => {
  const response = await request<{ workFiles: WorkFile[] }>("/api/workfiles", {
    method: "GET",
  });

  return response.workFiles;
};

export const createRemoteWorkFile = async (name: string) => {
  const response = await request<{ workFile: WorkFile }>("/api/workfiles", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

  return response.workFile;
};

export const loadRemoteWorkFile = async (workFileId: string) => {
  const response = await request<{
    workFile: WorkFile;
    scene: RemoteScene;
  }>(`/api/workfiles/${encodeURIComponent(workFileId)}`, {
    method: "GET",
  });

  markRemoteWorkFileFilesAsUploaded(workFileId, response.scene.files);
  lastPersistedSceneVersionByWorkFile.set(
    workFileId,
    getSceneVersion(response.scene.elements),
  );

  return {
    ...response,
    scene: {
      ...response.scene,
      appState: sanitizeRemoteAppStateForLoad(response.scene.appState),
    },
  };
};

export const renameRemoteWorkFile = async (
  workFileId: string,
  name: string,
) => {
  const response = await request<{ workFile: WorkFile }>(
    `/api/workfiles/${encodeURIComponent(workFileId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ name }),
    },
  );

  return response.workFile;
};

export const saveRemoteWorkFile = async (
  workFileId: string,
  scene: RemoteScene,
): Promise<WorkFile | null> => {
  const sceneVersion = getSceneVersion(scene.elements);
  const filesToUpload = prepareFilesForRemoteSave(workFileId, scene.files);

  if (
    !filesToUpload &&
    lastPersistedSceneVersionByWorkFile.get(workFileId) === sceneVersion
  ) {
    return null;
  }

  const payload: Record<string, unknown> = {
    elements: scene.elements,
    appState: scene.appState
      ? clearAppStateForDatabase(scene.appState)
      : null,
  };

  if (filesToUpload) {
    payload.files = filesToUpload;
  }

  const response = await request<{ workFile: WorkFile }>(
    `/api/workfiles/${encodeURIComponent(workFileId)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );

  if (filesToUpload) {
    markRemoteWorkFileFilesAsUploaded(workFileId, filesToUpload);
  }

  lastPersistedSceneVersionByWorkFile.set(workFileId, sceneVersion);

  return response.workFile;
};

export const deleteRemoteWorkFile = async (workFileId: string) => {
  const response = await request<{ workFiles: WorkFile[] }>(
    `/api/workfiles/${encodeURIComponent(workFileId)}`,
    {
      method: "DELETE",
    },
  );

  return response.workFiles;
};
